import clsx from "clsx";
import {
  DefaultButton,
  Dialog,
  DialogFooter,
  DialogType,
  DirectionalHint,
  Icon,
  IconButton,
  Label,
  Link,
  Persona,
  PersonaPresence,
  PersonaSize,
  PrimaryButton,
  Spinner,
} from "@fluentui/react";
import React, { useCallback, useEffect, useMemo, useState } from "react";
import DocViewer, { DocViewerRenderers } from "@cyntler/react-doc-viewer";
import "@cyntler/react-doc-viewer/dist/index.css";
import { useTranslation } from "react-i18next";
import { useDispatch } from "react-redux";
import { useLocation, useNavigate, useParams } from "react-router-dom";
import styled from "styled-components";
import { Flex } from "../../../../../components/styled";
import { useSelector } from "../../../../../store/hooks";
import { selectByIdLocations } from "../../../../../store/locations/locations";
import {
  assessmentShowHideActions,
  setDeleteAssessmentDialog,
} from "../../../../../store/recommendationPage";
import { AssessmentWithAttachments } from "../../../../../types/types";
import {
  formatDateString,
  getFileExtension,
  getFileIcon,
  getIconColor,
} from "../../../../../utils/utils";
import { ImprovementPotential } from "./ImprovementPotential";
import { StatusCompanyCode } from "./StatusCompanyCode";
import { StatusGrecoCode } from "./StatusGrecoCode";
import { TooltipComponent } from "components/TooltipComponent";
import { formatDateStringWithTime } from "utils/date";

type Props = {
  assessment: AssessmentWithAttachments;
};

export const Assessment = ({ assessment: r }: Props) => {
  const { t } = useTranslation();
  const location = useLocation();
  const taxonomy = useSelector((s) => s.taxonomy);
  const rec = useSelector((s) => s.recommendationPage.rec);
  const isWaitingForClient = rec?.recommendationStatusCode === 2;
  const isShowHideLoading = useSelector(
    (s) => s.recommendationPage.isShowHideLoading,
  );

  const { locationId } = useParams() as any;
  const urlSearchParams = new URLSearchParams(location.search);
  const assessmentId = urlSearchParams.get("assessmentId");
  const loc = useSelector((s) => selectByIdLocations(s, locationId));
  const isAssessmentPeriod = loc?.assessmentPeriodStartedAt ? true : false;
  const userRole = useSelector((s) => s.userRole.userRole);
  const dispatch = useDispatch();
  const [showinsurerButtons, setShowInsurerButtons] = React.useState(false);
  const [isPreviewDialogOpen, setIsPreviewDialogOpen] = useState(false);
  const [activeDocUri, setActiveDocUri] = useState<string | undefined>();

  const documents = useMemo(() => {
    return (
      r.assessmentAttachments
        ?.filter((att) => att.attachmentPath)
        .map((att) => ({
          uri: att.attachmentPath as string,
          fileName: att.attachmentName,
          fileType: att.attachmentMimeType || undefined,
        })) || []
    );
  }, [r.assessmentAttachments]);

  const uploadedAtByUri = useMemo(() => {
    const map = new Map<string, string>();
    r.assessmentAttachments
      ?.filter((att) => att.attachmentPath)
      .forEach((att) => {
        if (att.attachmentCreatedAt) {
          map.set(att.attachmentPath as string, String(att.attachmentCreatedAt));
        }
      });
    return map;
  }, [r.assessmentAttachments]);

  const handleDocumentChange = useCallback(
    (doc: { uri: string }) => setActiveDocUri(doc.uri),
    [],
  );

  const docViewerConfig = useMemo(
    () => ({
      header: { disableHeader: false, disableFileName: false },
      pdfZoom: { defaultZoom: 1, zoomJump: 0.1 },
      pdfVerticalScrollByDefault: true,
    }),
    [],
  );

  const docViewerElement = useMemo(
    () => (
      <DocViewer
        key={documents.map((d) => d.uri).join(",")}
        documents={documents}
        pluginRenderers={DocViewerRenderers}
        prefetchMethod="GET"
        onDocumentChange={handleDocumentChange}
        config={docViewerConfig}
      />
    ),
    [documents, handleDocumentChange, docViewerConfig],
  );

  const dialogContentProps = {
    type: DialogType.normal,
    title: t("ram.responsesPage.attachments.previewDocuments"),
    closeButtonAriaLabel: "Close",
  };

  useEffect(() => {
    if (userRole !== "insurer" && userRole !== "external") {
      setShowInsurerButtons(true);
    } else {
      setShowInsurerButtons(false);
    }
  }, [userRole]);

  const bubbleStyle = useMemo(() => {
    let retVal: any = {};
    if (r.assessment.hideFromInsurer) {
      retVal.border = "1px dashed #faa";
    }
    if (assessmentId === r.assessment.assessmentId + "") {
      retVal.borderWidth = "3px";
    } else {
      retVal.borderWidth = "1px";
    }
    return retVal;
  }, [r.assessment, assessmentId]);

  const getTaxonomyValue = (
    taxonomyKey: string,
    code: number | null | undefined,
  ) => {
    if (code === null || code === undefined) return null;
    const taxonomyItem = taxonomy[taxonomyKey]?.byId?.[code];
    return taxonomyItem ? t(taxonomyItem.code) : null;
  };

  useEffect(() => {
    if (
      document.getElementById("assessment_" + assessmentId) &&
      document.getElementById("assessments-tab-description")
    ) {
      document.getElementById("assessments-tab").scrollTop =
        document.getElementById("assessment_" + assessmentId).offsetTop +
        document.getElementById("assessments-tab-description").offsetHeight +
        50;
    }
  }, [assessmentId, rec]);

  return (
    <Root
      id={"assessment_" + r.assessment.assessmentId}
      data-assessmentid={r.assessment.assessmentId}
      className={clsx({
        draft: !r.assessment.isArchived,
        "not-draft": r.assessment.isArchived,
      })}
    >
      <Persona
        title={
          (r.assessment.assessmentCreatedByUserName ?? "") +
          "\n" +
          t("ram.clientsList.lastModifiedAt") +
          " " +
          formatDateStringWithTime(r.assessment.assessmentLastModifiedAt)
        }
        text={r.assessment.assessmentCreatedByUserName ?? ""}
        presence={PersonaPresence.online}
        styles={{
          root: {
            width: "40px",
            alignSelf: "flex-start",
            marginTop: "10px",
          },
        }}
        size={PersonaSize.size40}
      />
      <Bubble style={bubbleStyle}>
        <AdjustmentIndicator $isAdjustment={r.assessment.isAdjustment}>
          <Top>
            <BubbleWrap>
              <div>
                <Label>
                  {r.assessment.isAdjustment
                    ? t("ram.assessmentsPage.dateOfAdjustment")
                    : t("ram.assessmentsPage.dateOfAssessment")}
                </Label>
                {r.assessment.dateOfAssessment &&
                  formatDateString(r.assessment.dateOfAssessment)}
              </div>
              <ImprovementPotential
                improvementPotentialCode={r.assessment.improvementPotentialCode}
              />
              {getTaxonomyValue(
                "RiskImprovementPotential",
                r.assessment.improvementPotentialCodeAsIf,
              ) && (
                <div>
                  <Label>
                    {t(
                      "ram.recommendationForm.improvementPotentialCodeAsIf.label",
                    )}
                  </Label>
                  <span>
                    {getTaxonomyValue(
                      "RiskImprovementPotential",
                      r.assessment.improvementPotentialCodeAsIf,
                    )}
                  </span>
                </div>
              )}
            </BubbleWrap>

            <Flex direction="column" gap={5}>
              <Flex
                direction="column"
                style={{
                  visibility:
                    userRole === "admin" ||
                    userRole === "internal" ||
                    userRole === "internal_role" ||
                    userRole === "external_advanced"
                      ? "visible"
                      : "hidden",
                }}
              >
                <PrimaryButton
                  text={t("greco.delete")}
                  iconProps={{ iconName: "Trash" }}
                  onClick={() => {
                    dispatch(
                      setDeleteAssessmentDialog({
                        isOpen: true,
                        assessment: r.assessment,
                      }),
                    );
                  }}
                />
              </Flex>
            </Flex>
          </Top>
          <Middle>
            <div
              style={{
                display: "flex",
                alignItems: "center",
                gap: "25px",
              }}
            >
              <StatusCompanyCode
                statusCompany={r.assessment.statusCompanyCode}
              />
              <StatusGrecoCode statusGrECo={r.assessment.statusGrEcoCode} />
            </div>
          </Middle>
          <div
            style={{
              paddingTop: "10px",
            }}
          >
            {r.assessment.isAdjustment && (
              <AdjustmentFieldsGrid>
                {getTaxonomyValue(
                  "RiskPriority",
                  r.assessment.priorityCode,
                ) && (
                  <AdjustmentField>
                    <Label>
                      {t("ram.recommendationForm.priorityCode.label")}
                    </Label>
                    <span>
                      {getTaxonomyValue(
                        "RiskPriority",
                        r.assessment.priorityCode,
                      )}
                    </span>
                  </AdjustmentField>
                )}
                {getTaxonomyValue(
                  "RiskPriority",
                  r.assessment.priorityCompanyCode,
                ) && (
                  <AdjustmentField>
                    <Label>
                      {t("ram.recommendationForm.priorityCompanyCode.label")}
                    </Label>
                    <span>
                      {getTaxonomyValue(
                        "RiskPriority",
                        r.assessment.priorityCompanyCode,
                      )}
                    </span>
                  </AdjustmentField>
                )}
                {getTaxonomyValue(
                  "RiskCategoryOfMeasure",
                  r.assessment.typeCode,
                ) && (
                  <AdjustmentField>
                    <Label>{t("ram.recommendationForm.typeCode.label")}</Label>
                    <span>
                      {getTaxonomyValue(
                        "RiskCategoryOfMeasure",
                        r.assessment.typeCode,
                      )}
                    </span>
                  </AdjustmentField>
                )}
                {getTaxonomyValue(
                  "RiskAssessmentSubcategory",
                  r.assessment.subCategory1Code,
                ) && (
                  <AdjustmentField>
                    <Label>
                      {t("ram.recommendationForm.subCategory1Code.label")}
                    </Label>
                    <span>
                      {getTaxonomyValue(
                        "RiskAssessmentSubcategory",
                        r.assessment.subCategory1Code,
                      )}
                    </span>
                  </AdjustmentField>
                )}
                {r.assessment.weighting !== null &&
                  r.assessment.weighting !== undefined && (
                    <AdjustmentField>
                      <Label>
                        {t("ram.recommendationForm.weighting.label")}
                      </Label>
                      <span>{r.assessment.weighting}</span>
                    </AdjustmentField>
                  )}
                {getTaxonomyValue(
                  "RiskImprovementPotential",
                  r.assessment.improvementPotentialCodeAsIf,
                ) && (
                  <AdjustmentField>
                    <Label>
                      {t(
                        "ram.recommendationForm.improvementPotentialCodeAsIf.label",
                      )}
                    </Label>
                    <span>
                      {getTaxonomyValue(
                        "RiskImprovementPotential",
                        r.assessment.improvementPotentialCodeAsIf,
                      )}
                    </span>
                  </AdjustmentField>
                )}
                {getTaxonomyValue(
                  "RiskReductionCost",
                  r.assessment.costEstimateCode,
                ) && (
                  <AdjustmentField>
                    <Label>
                      {t("ram.recommendationForm.costEstimateCode.label")}
                    </Label>
                    <span>
                      {getTaxonomyValue(
                        "RiskReductionCost",
                        r.assessment.costEstimateCode,
                      )}
                    </span>
                  </AdjustmentField>
                )}
              </AdjustmentFieldsGrid>
            )}
            <div
              style={{
                paddingTop: "10px",
              }}
            >
              <Label>{t("ram.assessmentsPage.comment")}</Label>
              <div dangerouslySetInnerHTML={{ __html: r.assessment.comment }} />
            </div>
            {r.assessmentAttachments.length !== 0 && (
              <div>
                <AttachmentLabelRow>
                  <Label>{t("ram.assessmentsPage.attachments")}</Label>
                  {documents.length > 0 && (
                    <PreviewIconButton
                      iconProps={{ iconName: "View" }}
                      title={t(
                        "ram.responsesPage.attachments.previewDocuments",
                      )}
                      ariaLabel={t(
                        "ram.responsesPage.attachments.previewDocuments",
                      )}
                      onClick={() => {
                        setActiveDocUri(documents[0]?.uri);
                        setIsPreviewDialogOpen(true);
                      }}
                    />
                  )}
                </AttachmentLabelRow>
                {r.assessmentAttachments?.map((a) => {
                  return (
                    <TooltipComponent
                      key={a.attachmentId}
                      content={a.attachmentName}
                      directionalHint={DirectionalHint.leftTopEdge}
                    >
                      <div
                        key={a.attachmentId}
                        style={{
                          display: "flex",
                          alignItems: "center",
                          marginLeft: "10px",
                          marginBottom: "10px",
                        }}
                      >
                        <Icon
                          iconName={getFileIcon(
                            getFileExtension(a.attachmentName),
                          )}
                          style={{
                            color: getIconColor(
                              getFileExtension(a.attachmentName),
                            ),
                          }}
                          styles={{
                            root: {
                              marginRight: "5px",
                              fontSize: "14px",
                            },
                          }}
                        ></Icon>
                        <Link
                          styles={{
                            root: {
                              fontSize: "14px",
                              overflow: "hidden",
                              textOverflow: "ellipsis",
                              whiteSpace: "nowrap",
                              flex: 1,
                            },
                          }}
                          onClick={() => {
                            window.open(a.attachmentPath);
                          }}
                        >
                          {a.attachmentName}
                        </Link>
                      </div>
                    </TooltipComponent>
                  );
                })}
              </div>
            )}

            <Dialog
              hidden={!isPreviewDialogOpen}
              onDismiss={() => setIsPreviewDialogOpen(false)}
              dialogContentProps={dialogContentProps}
              minWidth="80vw"
              maxWidth="90vw"
              modalProps={{
                isBlocking: false,
                styles: { main: { maxWidth: "90vw" } },
              }}
            >
              <div style={{ display: "flex", alignItems: "center", marginBottom: "10px", justifyContent: "space-between" }}>
                <div style={{ display: "flex", alignItems: "center", gap: "6px" }}>
                  <Label style={{ margin: 0 }}>{t("ram.assessmentsPage.dateOfAssessment")}:</Label>
                  <span>
                    {r.assessment.dateOfAssessment
                      ? formatDateString(r.assessment.dateOfAssessment)
                      : ""}
                  </span>
                </div>
                {activeDocUri && uploadedAtByUri.get(activeDocUri) && (
                  <div style={{ display: "flex", alignItems: "center", gap: "6px" }}>
                    <Label style={{ margin: 0 }}>
                      {t("ram.responsesPage.attachments.uploadedAt")}:
                    </Label>
                    <span>
                      {formatDateStringWithTime(uploadedAtByUri.get(activeDocUri)!)}
                    </span>
                  </div>
                )}
              </div>
              <DocViewerContainer>
                {docViewerElement}
              </DocViewerContainer>
              <DialogFooter>
                <DefaultButton
                  onClick={() => setIsPreviewDialogOpen(false)}
                  text={t("ram.reportDocumentDialogDownloadClose.label")}
                />
              </DialogFooter>
            </Dialog>
          </div>
          {showinsurerButtons ? (
            isShowHideLoading ? (
              <InsurerCheckboxSection>
                <InsurerSpinner>
                  <Spinner />
                </InsurerSpinner>
              </InsurerCheckboxSection>
            ) : (
              <InsurerCheckboxSection>
                <InsurerCheckboxField
                  onClick={() => {
                    if (isShowHideLoading) return;
                    dispatch(
                      assessmentShowHideActions.trigger({
                        assessment: r.assessment,
                      }),
                    );
                  }}
                >
                  {r.assessment.hideFromInsurer
                    ? t("ram.assessmentPage.hiddenFromInsurer")
                    : t("ram.assessmentPage.displayedToInsurer")}
                </InsurerCheckboxField>
              </InsurerCheckboxSection>
            )
          ) : null}{" "}
        </AdjustmentIndicator>{" "}
      </Bubble>
    </Root>
  );
};

const InsurerCheckboxSection = styled.div`
  padding: 10px 0;
  border-top: 1px solid ${(p) => p.theme.palette.neutralTertiaryAlt};
`;

const InsurerSpinner = styled.div`
  float: right;
  text-align: right;
  margin-left: auto;
  margin-right: 45px;
  padding: 8px 15px;
  font-size: 14px;
  color: ${(p) => p.theme.palette.neutralSecondary};
  &:hover {
    // text-decoration: underline;
    color: ${(p) => p.theme.palette.neutralPrimary};
    cursor: pointer;
  }
`;

const InsurerCheckboxField = styled.div`
  width: min-content;
  text-align: right;
  margin-left: auto;
  padding: 8px 15px;
  font-size: 14px;
  border: 1px solid ${(p) => p.theme.palette.neutralTertiaryAlt};
  color: ${(p) => p.theme.palette.neutralSecondary};
  &:hover {
    // text-decoration: underline;
    color: ${(p) => p.theme.palette.neutralPrimary};
    cursor: pointer;
  }
`;

const AdjustmentIndicator = styled.div<{ $isAdjustment: boolean }>`
  border-left: ${(p) => (p.$isAdjustment ? "10px solid #fff176" : "none")};
  margin: ${(p) => (p.$isAdjustment ? "-15px -20px" : "0")};
  padding: ${(p) => (p.$isAdjustment ? "15px 20px 15px 10px" : "0")};
`;

const AdjustmentFieldsContainer = styled.div`
  padding: 10px;
  margin-bottom: 10px;
  background-color: ${(p) => p.theme.palette.neutralLighter};
  border: 1px solid ${(p) => p.theme.palette.neutralTertiaryAlt};
  border-radius: 4px;
`;

const AdjustmentFieldsGrid = styled.div`
  display: grid;
  grid-template-columns: repeat(auto-fill, minmax(200px, 1fr));
  gap: 10px;
`;

const AdjustmentField = styled.div`
  display: flex;
  flex-direction: column;
  gap: 2px;

  & > span {
    font-size: 13px;
  }
`;

const Top = styled.div`
  display: flex;
  align-items: center;
  border-bottom: 1px solid ${(p) => p.theme.palette.neutralTertiaryAlt};
  padding-bottom: 10px;
  @media (max-width: 700px) {
    padding-bottom: 0;
  }
`;

const Middle = styled.div`
  padding: 10px 0;
  border-bottom: 1px solid ${(p) => p.theme.palette.neutralTertiaryAlt};
`;
const BubbleWrap = styled.div`
  display: grid;
  grid-template-columns: 1fr 1fr 1fr;
  flex: 1;
  gap: 10px;
  @media (max-width: 700px) {
    grid-template-columns: 1fr;
    > *:last-child {
      margin-bottom: 10px;
    }
  }
`;

export const Bubble = styled.div`
  padding: 15px 20px;
  border: 1px solid ${(p) => p.theme.palette.neutralTertiaryAlt};
  max-width: 600px;
  flex: 1;
  background-color: ${(p) => p.theme.palette.white};
`;

export const Root = styled.div`
  width: 100%;
  display: flex;
  align-items: center;
  margin-bottom: 15px;
  padding: 0 15px;
  gap: 10px;
  font-size: 14px;
  background-color: ${(p) => p.theme.palette.white};
  color: ${(p) => p.theme.palette.neutralPrimary};
  @media (max-width: 500px) {
    flex-direction: column;
    &&.draft {
      flex-direction: column;
    }
  }
  &.not-draft {
    justify-content: center;
    ${Bubble} {
      background-color: ${(p) => p.theme.palette.neutralLighter};
      justify-self: flex-end;
      border-radius: 0px 0px 0px 0px;
    }
  }
  &.draft {
    justify-content: center;
    flex-direction: row-reverse;
    ${Bubble} {
      background-color: ${(p) => p.theme.palette.neutralLighter};
      justify-self: flex-end;
      border-radius: 0px 0px 0px 0px;
    }
  }
`;

const AttachmentLabelRow = styled.div`
  display: flex;
  align-items: center;
  gap: 8px;
`;

const PreviewIconButton = styled(IconButton)`
  height: 24px;
  width: 24px;

  i {
    font-size: 14px;
  }
`;

const DocViewerContainer = styled.div`
  width: 100%;
  height: 80vh;
  min-height: 600px;
  overflow: auto;

  #react-doc-viewer {
    height: 100% !important;
  }

  #proxy-renderer {
    height: 100% !important;
  }

  #msdoc-renderer,
  #image-renderer,
  #pdf-renderer {
    height: 100% !important;
  }

  iframe {
    height: 100% !important;
    min-height: 550px;
  }
`;
